<template>
  <AppDialogContent
    v-model="dialogValue"
    :disabled="formSubmitted"
    :loading="formSubmitted"
    :persistent="formSubmitted"
    :disabled-submit="v$.form.$invalid || invalidHorseCannotSubmit"
    custom-header
    @submit-form="submit"
  >
    <template #header>
      <div class="d-flex justify-space-between">
        <div v-if="!!responseType">
          <div class="text-h5 font-weight-bold">
            {{ responseTexts.headline }}
          </div>
        </div>
        <div v-else>
          <div class="text-h5 font-weight-bold mb-2">
            {{
              $t(
                "horses:horseAction.horseCreateInFei.heading",
                "Get FEI ID (add horse on FEI database)"
              )
            }}
          </div>
          <p>
            {{
              $t(
                "horses:horseAction.horseCreateInFei.explanation.text",
                "You are going to add this horse to FEI database. Please check the data below. Action is only possible, if data is complete. Check the data correctness and completeness to proceed."
              )
            }}
          </p>
        </div>
        <div class="float-end">
          <v-btn-icon
            icon
            :disabled="formSubmitted"
            class="mx-0"
            data-test="closeDialog"
            :aria-label="$t('common:closeButton.alt')"
            @click="() => (dialogValue = false)"
          >
            <v-icon>$iconTimesCircle</v-icon>
          </v-btn-icon>
        </div>
      </div>
    </template>

    <template #default>
      <div v-if="!!responseType">
        <EntryFeiResponse
          :response-type="responseType"
          :feedback-messages="feedbackMessages"
          :mutation-errors="mutationErrors"
          :content-align="responseType === 'warning' ? null : undefined"
        >
          <template v-if="responseType === 'warning'" #custom-content>
            <div class="px-4">
              <v-row>
                <v-col cols="12" sm="4" md="2" class="ma-auto text-center">
                  <v-icon size="x-large"> $iconBellRinged</v-icon>
                </v-col>
                <v-col cols="12" sm="8" md="10">
                  {{
                    $t(
                      $t(
                        "horses:horseAction.getFEIID.potentialDuplicatesCheck.mainMessage.text",
                        "FEI database returned the following suggestions for possible duplicates of a horse that you asked to add to the database. Please review the following potential duplicate horses before proceeding:"
                      )
                    )
                  }}
                </v-col>
              </v-row>
              <br />

              <div
                v-for="(
                  { duplicate, ruleDescription }, index
                ) in horseDuplicates"
                :key="index"
                class="mb-4"
              >
                {{ duplicate.currentName }} / {{ duplicate.feiCode }} /
                {{ duplicate.ueln }} / {{ duplicate.microchip }} /
                {{ $dayjs.date(duplicate.dateBirth) }} /
                {{ duplicate.studBookCode }} / {{ duplicate.horseSireName }} /
                {{ duplicate.horseDamName }} /
                {{ duplicate.horseSireOfDamName }} /
                {{ duplicate.birthCountryCode }}
                <div class="text-red">
                  <small>{{ ruleDescription }}</small>
                </div>
              </div>

              <AppAlertNotice center>
                {{
                  $t(
                    "horses:horseAction.getFEIIDfailed.potentialDuplicatesCheck.text",
                    "Click on Cancel, if horse already exists in FEI database and can be identified from a list provided above. Add the FEI ID of this horse to the horse´s profile in your system. Click on Continue if you confirm that no duplicates found and you would like to request the horse´s addition to the FEI system."
                  )
                }}
              </AppAlertNotice>
            </div>
          </template>

          <template #default>
            {{ responseTexts.main }}
          </template>

          <template #reason>
            <div v-if="responseType == 'error'">
              {{ responseTexts.reason }}<br />
            </div>
          </template>
          <template #additional-message>
            {{ responseTexts.additional }}
          </template>
        </EntryFeiResponse>
      </div>
      <div v-else>
        <v-progress-linear
          v-if="$apollo.queries.initHorseCreateInFeiForm.loading"
          indeterminate
        />

        <template v-else-if="dialogValue && horseFromInitForm">
          <div class="mb-5">
            <h2 class="text-h6 font-weight-bold mb-2">
              {{
                $t(
                  "horses:horseAction.horseCreateInFei.identifiers.subheading",
                  "Name and identifiers"
                )
              }}
            </h2>

            <div class="mb-1">
              {{
                $t(
                  "horses:horseAction.horseCreateInFei.birthName.label",
                  "Birth name:"
                )
              }}
              <component
                :is="showValueOrMissingBadge(horseFromInitForm.birthName)"
              />
            </div>
            <div class="mb-1">
              {{
                $t(
                  "horses:horseAction.horseCreateInFei.birthName.label",
                  "Current name:"
                )
              }}
              <component
                :is="showValueOrMissingBadge(horseFromInitForm.name)"
              />
            </div>
            <div class="mb-1">
              {{
                $t(
                  "horses:horseAction.horseCreateInFei.licenceNumber.label",
                  "National ID:"
                )
              }}
              <component
                :is="showValueOrMissingBadge(horseFromInitForm.licenceNumber)"
              />
            </div>
            <div class="mb-1">
              {{
                $t("horses:horseAction.horseCreateInFei.ueln.label", "UELN:")
              }}
              <component
                :is="showValueOrMissingBadge(horseFromInitForm.ueln)"
              />
            </div>
            <div class="mb-1">
              {{
                $t(
                  "horses:horseAction.horseCreateInFei.passportNumber.label",
                  "FEI recognized document ID:"
                )
              }}
              <component
                :is="showValueOrMissingBadge(horseFromInitForm.passportNumber)"
              />
            </div>
            <div class="mb-1">
              {{
                $t(
                  "horses:horseAction.horseCreateInFei.passportDocumentIssuingBody.label",
                  "Issuing body for document:"
                )
              }}

              <component
                :is="
                  showPassportDocumentIssuingBodyOrMissingBadge(
                    horseFromInitForm.passportDocumentIssuingBody
                  )
                "
              />
            </div>
            <div class="mb-1">
              {{
                $t(
                  "horses:horseAction.horseCreateInFei.passportDocumentIssuingBody.label",
                  "Microchip:"
                )
              }}
              <component
                :is="showValueOrMissingBadge(horseFromInitForm.chipNumber)"
              />
            </div>
          </div>

          <div class="mb-5">
            <h2 class="text-h6 font-weight-bold mb-2">
              {{
                $t(
                  "horses:horseAction.horseCreateInFei.background.subheading",
                  "Background information"
                )
              }}
            </h2>

            <div class="mb-1">
              {{
                $t(
                  "horses:horseAction.horseCreateInFei.dateOfBirth.label",
                  "Date of birth:"
                )
              }}
              <template v-if="horseFromInitForm.dateOfBirth">
                {{ $dayjs.date(horseFromInitForm.dateOfBirth) }}
              </template>
              <component :is="showValueOrMissingBadge(null)" v-else />
            </div>

            <div class="mb-1">
              {{
                $t(
                  "horses:horseAction.horseCreateInFei.birthCountry.label",
                  "Birth country:"
                )
              }}
              <template v-if="horseFromInitForm.birthCountryId">
                {{ horseFromInitForm.birthCountry.name }}
              </template>
              <component :is="showValueOrMissingBadge(null)" v-else />
            </div>

            <div class="mb-1">
              {{
                $t(
                  "horses:horseAction.horseCreateInFei.gender.label",
                  "Gender:"
                )
              }}
              <template v-if="horseFromInitForm.gender">
                {{
                  $t(
                    "common:horse.gender." + horseFromInitForm.gender,
                    horseFromInitForm.gender
                  )
                }}
              </template>
              <component :is="showValueOrMissingBadge(null)" v-else />
            </div>
            <div class="mb-1">
              {{
                $t(
                  "horses:horseAction.horseCreateInFei.colourCodeAndComplement.label",
                  "Color Code and Complement:"
                )
              }}
              <component
                :is="showValueOrMissingBadge(horseColorText(horseFromInitForm))"
              />
            </div>
            <div class="mb-1">
              {{
                $t(
                  "horses:horseAction.horseCreateInFei.isPony.label",
                  "Is pony"
                )
              }}
              {{
                $t(
                  "horses:horseAction.horseCreateInFei.isPony." +
                    (horseFromInitForm.horseType == "pony") +
                    ".text",
                  String(horseFromInitForm.horseType == "pony")
                )
              }}
            </div>
          </div>

          <div class="mb-5">
            <h2 class="text-h6 font-weight-bold mb-2">
              {{
                $t(
                  "horses:horseAction.horseCreateInFei.ownership.subheading",
                  "Ownership"
                )
              }}
            </h2>

            <div class="mb-2">
              {{
                $t(
                  "horses:horseAction.horseCreateInFei.ownershipSince.label",
                  "Ownership since:"
                )
              }}
              {{ $dayjs.date(horseFromInitForm.ownershipSince) }}
            </div>

            <div class="mb-1">
              <div class="mb-2">
                <b
                  >{{
                    $t(
                      "horses:horseAction.horseCreateInFei.owners.subheading",
                      "List of owners:"
                    )
                  }}
                </b>
              </div>
              <ul class="pl-0 ml-0" style="list-style: none">
                <li
                  v-for="owner in horseFromInitForm.currentOwners"
                  :key="owner.id"
                  class="mb-1"
                >
                  <template v-if="owner.personId">
                    <AppShowPersonName :person="owner.person" />&nbsp;
                    <AppShowCountry
                      :country="owner.person.representingCountry"
                      :show-ioc="false"
                    />
                  </template>

                  <template v-else-if="owner.organisationId">
                    <AppShowOrganisationName
                      :organisation="owner.organisation"
                    />&nbsp;
                    <AppShowCountry
                      :country="owner.organisation.registryCountry"
                      :show-ioc="false"
                    />
                  </template>

                  <template v-else>
                    <span>{{ owner.firstName }} {{ owner.lastName }}</span
                    >&nbsp;
                    <AppShowCountry
                      :country="owner.registryCountry"
                      :show-ioc="false"
                    />
                  </template>
                  <span v-if="owner.currentFeiid" class="ml-1"
                    >({{ owner.currentFeiid }})</span
                  ><AppChip v-else color="red" text-color="text-white">
                    {{
                      $t(
                        "horses:horseAction.horseCreateInFei.FEIIDMissing.badge",
                        "Missing FEI ID"
                      )
                    }} </AppChip
                  >,
                  <span v-if="owner.ownershipShare">
                    {{ returnLocalisedPercent(owner.ownershipShare) }} </span
                  ><AppChip v-else color="red" text-color="text-white">
                    {{
                      $t(
                        "horses:horseAction.horseCreateInFei.percentageNotSet.badge",
                        "Ownership percentage not set"
                      )
                    }}
                  </AppChip>

                  <AppChip
                    v-if="missingRequiredAddress(owner)"
                    color="red"
                    text-color="text-white"
                  >
                    {{
                      $t(
                        "horses:horseAction.horseCreateInFei.addressMisingOrIncomplete.badge",
                        "Address missing or incomplete"
                      )
                    }}
                  </AppChip>
                </li>
              </ul>
            </div>
          </div>

          <div v-if="v$.form.representingCountryId" class="mb-5">
            <h2 class="text-h6 font-weight-bold mb-2">
              {{
                $t(
                  "horses:horseAction.horseCreateInFei.selectNationalityOfOwnership.subheading",
                  "Select nationality of ownership:"
                )
              }}
            </h2>

            <AppSelectCountry
              v-model="form.representingCountryId"
              :input-props="{
                label: $t(
                  'horses:horseAction.horseCreateInFei.representingCountryId.label',
                  'Nationality of ownership'
                ),
                hint: $t(
                  'horses:horseAction.horseCreateInFei.representingCountryId.hint',
                  'Nationality should be determined based on nationalities of owners. Check with owners, if you have any doubts.'
                ),
                persistentHint: true,
                clearable: true
              }"
              data-test="representingCountryId"
              @blur="v$.form.representingCountryId.$touch()"
            />
          </div>

          <div class="mb-1">
            <b>{{
              $t(
                "horses:horseAction.horseCreateInFei.alertNote.heading",
                "Please note"
              )
            }}</b>
          </div>

          <AppAlertNotice center>
            {{
              $t(
                "horses:horseAction.horseCreateInFei.alertNote.text",
                "You can proceed and press on “Send data to FEI” only after all required fields are added. Identifiers and background information can be checked and edit from horse´s edit form. Ownership information - from Owners (Responsibles) tab. Before adding a horse to FEI database, you may need to add all current owners to FEI database and add FEI ID-s under their respective profiles. "
              )
            }}
          </AppAlertNotice>
        </template>
      </div>
    </template>

    <template #submit>
      {{
        $t(
          "horses:horseAction.horseCreateInFei.sendDataToFEI.button",
          "Send data to FEI"
        )
      }}
    </template>

    <template v-if="!!responseType" #actions>
      <v-row
        :style="responseType == 'warning' ? '' : 'justify-content: flex-end'"
      >
        <template v-if="responseType == 'warning'">
          <v-col cols="12" md="6">
            <v-btn
              rounded
              variant="outlined"
              :disabled="formSubmitted"
              min-width="180px"
              block
              data-test="dialogClose"
              @click="() => (dialogValue = false)"
            >
              {{ $t("common:button.cancel", "Cancel") }}
            </v-btn>
          </v-col>
          <v-col cols="12" md="6">
            <v-btn
              rounded
              :loading="formSubmitted"
              variant="outlined"
              block
              data-test="dialogSubmit"
              min-width="180px"
              @click="() => submit()"
            >
              {{ $t("common:button.confirm", "Confirm") }}
            </v-btn>
          </v-col>
        </template>
        <div v-else class="mb-1 text-center text-md-right">
          <v-btn
            rounded
            variant="outlined"
            min-width="180px"
            data-test="dialogClose"
            @click="() => (dialogValue = false)"
          >
            {{ responseTexts.okBtn }}
          </v-btn>
        </div>
      </v-row>
    </template>
  </AppDialogContent>
</template>
<script>
import { defineComponent, h, ref } from "vue";
import gql from "fraql";
import { useFormValidation } from "@/composables/form/form_validation";
import {
  useDisplayText,
  DisplayTextFragments
} from "@composables/display_text";
import { useHorseHelper } from "@composables/horse_helper";
import AppDialogContent from "@elements/AppDialogContent.vue";
import AppAlertNotice from "@elements/AppAlertNotice/index.vue";
import EntryFeiResponse from "@elements/AppEntryFeiActions/EntryFeiResponse.vue";
import AppSelectCountry from "@/components/elements/AppSelects/AppSelectCountry.vue";
import AppShowPersonName from "@AppShows/person/AppShowPersonName.vue";
import AppShowOrganisationName from "@AppShows/organisation/AppShowOrganisationName.vue";
import AppShowCountry from "@AppShows/AppShowCountry.vue";
import { getFederationConfiguration } from "@composables/masterdata/config";
import AppChip from "@elements/AppChip.vue";
import { useTranslation } from "i18next-vue";

export default defineComponent({
  name: "HorseManualFeiForm",
  components: {
    AppDialogContent,
    AppAlertNotice,
    EntryFeiResponse,
    AppSelectCountry,
    AppShowCountry,
    AppShowOrganisationName,
    AppShowPersonName,
    AppChip
  },
  props: {
    horse: {
      type: Object,
      required: true
    }
  },
  setup() {
    const { t } = useTranslation();

    const { displayText } = useDisplayText();
    const { horseColorText } = useHorseHelper();

    const { returnLocalisedPercent } = getFederationConfiguration();

    const form = ref({
      representingCountryId: null
    });

    const initHorseCreateInFeiForm = ref({
      horse: null,
      vuelidateValidations: {},
      readOnlyFields: [],
      formFields: []
    });

    const { v$, fieldDisabled, fieldErrorMessages } = useFormValidation(
      initHorseCreateInFeiForm,
      form
    );

    function showValueOrMissingBadge(val) {
      if (val)
        return {
          render() {
            return h("span", val);
          }
        };
      return {
        render() {
          return h(
            AppChip,
            {
              color: "red",
              textColor: "text-white"
            },
            () =>
              t(
                "horses:horseAction.horseCreateInFei.missingData.badge",
                "data missing"
              )
          );
        }
      };
    }

    function showPassportDocumentIssuingBodyOrMissingBadge(passport) {
      if (!passport) return showValueOrMissingBadge(null);

      const txt = `${passport.feiCode} - ${passport.name}`;
      return showValueOrMissingBadge(txt);
    }

    function missingRequiredAddress(owner) {
      let addressPieces = [];
      if (owner.personId) {
        addressPieces = [
          owner.person?.address?.addressText,
          owner.person?.address?.addressIndex,
          owner.person?.address?.addressCity,
          owner.person?.address?.addressCountryId
        ];
      } else if (owner.organisationId) {
        addressPieces = [
          owner.organisation?.address?.addressText,
          owner.organisation?.address?.addressIndex,
          owner.organisation?.address?.addressCity,
          owner.organisation?.address?.addressCountryId
        ];
      } else {
        addressPieces = [
          owner.addressText,
          owner.addressIndex,
          owner.addressCity,
          owner.addressCountryId
        ];
      }

      if (addressPieces.filter(Boolean).length != 4) {
        return true;
      }
      return false;
    }

    return {
      form,
      initHorseCreateInFeiForm,
      v$,
      fieldDisabled,
      fieldErrorMessages,

      displayText,
      horseColorText,
      returnLocalisedPercent,
      showValueOrMissingBadge,
      showPassportDocumentIssuingBodyOrMissingBadge,
      missingRequiredAddress
    };
  },
  data() {
    return {
      confirmationToken: null,
      value: false,
      formSubmitted: false,
      horseDuplicates: [],
      responseType: null,
      feedbackMessages: [],
      mutationErrors: [],
      formError: {}
    };
  },
  apollo: {
    initHorseCreateInFeiForm: {
      query: gql`
        query initHorseCreateInFeiForm($horseId: ID) {
          initHorseCreateInFeiForm(horseId: $horseId) {
            horse {
              id
              birthName
              name
              licenceNumber
              ueln
              passportNumber
              passportDocumentIssuingBodyId
              passportDocumentIssuingBody @client {
                id
                feiCode
                name
              }
              chipNumber
              dateOfBirth
              birthCountryId
              birthCountry @client {
                id
                name
              }
              gender
              colorId
              color @client {
                id
                name
              }
              colorComplement
              horseType
              ownershipSince
              currentOwners {
                id
                personId
                person {
                  id
                  ${AppShowPersonName.fragments.person}
                  address {
                    id
                    addressText
                    addressIndex
                    addressCity
                    addressCountryId
                  }
                  representingCountryId
                  representingCountry @client {
                    id
                    ${AppShowCountry.fragments.country}
                  }
                }
                organisationId
                organisation {
                  id
                  ${AppShowOrganisationName.fragments.organisation}
                  address {
                    id
                    addressText
                    addressIndex
                    addressCity
                    addressCountryId
                  }
                  registryCountryId
                  registryCountry @client {
                    id
                    ${AppShowCountry.fragments.country}
                  }
                }
                firstName
                lastName
                registryCountryId
                registryCountry @client {
                  id
                  ${AppShowCountry.fragments.country}
                }
                currentFeiid
                addressText
                addressIndex
                addressCity
                addressCountryId
                ownershipShare
              }
            }
            vuelidateValidations
            readOnlyFields
          }
        }
      `,
      skip() {
        return this.dialogValue == false;
      },
      variables() {
        return {
          horseId: this.horse.id
        };
      },
      manual: true,
      result({ data, loading }) {
        if (loading) return;
        this.initHorseCreateInFeiForm = data.initHorseCreateInFeiForm;
        this.v$.form.$touch();
      }
    }
  },
  computed: {
    invalidHorseCannotSubmit() {
      const horse = this.horseFromInitForm;
      if (!horse) return true;
      let cannotSubmit = false;

      [
        "birthName",
        "name",
        "licenceNumber",
        "ueln",
        "passportNumber",
        "passportDocumentIssuingBodyId",
        "chipNumber",
        "dateOfBirth",
        "birthCountryId",
        "colorId"
      ].some(k => {
        if (horse[k] == null) cannotSubmit = true;
        return cannotSubmit;
      });

      if (cannotSubmit) return true;

      (horse.currentOwners ?? []).some(owner => {
        if (owner.ownershipShare == null) {
          cannotSubmit = true;
        }
        if (owner.currentFeiid == null) {
          cannotSubmit = true;
        }

        if (!cannotSubmit) {
          cannotSubmit = this.missingRequiredAddress(owner);
        }

        return cannotSubmit;
      });

      return cannotSubmit;
    },
    horseFromInitForm() {
      return this.initHorseCreateInFeiForm.horse;
    },
    dialogValue: {
      get() {
        return this.value;
      },
      set(v) {
        if (v == true) {
          this.confirmationToken = null;
          this.form = {
            representingCountryId: null
          };
          this.horseDuplicates = [];
        }
        this.value = v;
        if (v == false) {
          this.responseType = null;
          this.feedbackMessages = [];
          this.mutationErrors = [];
        }
      }
    },
    responseTexts() {
      switch (this.responseType) {
        case "success":
          return {
            headline: this.$t(
              "horses:horseAction.getFEIID.success.heading",
              "Add horse to FEI database and get FEI ID"
            ),
            main: this.$t(
              "horses:horseAction.getFEIID.success.mainMessage.text",
              "Horse added successfully and FEI ID issued"
            ),
            additional: this.$t(
              "horses:horseAction.getFEIID.success.additionalMessage.text",
              "Please note, automatic notifications may be scheduled on this action to inform owners or horse´s responsibles on issuing an FEI ID for the horse."
            ),
            okBtn: this.$t("common:ok.button", "OK")
          };
        case "warning":
          return {
            headline: this.$t(
              "horses:horseAction.getFEIID.potentialDuplicatesCheck.heading",
              "Check potential duplicate horses´ records before adding a new horse"
            ),
            main: this.$t(
              "horses:horseAction.getFEIID.potentialDuplicatesCheck.mainMessage.text",
              "FEI database returned the following suggestions for possible duplicates of a horse that you asked to add to the database. Please review the following potential duplicate horses before proceeding:"
            )
          };
        case "error":
          return {
            headline: this.$t(
              "horses:horseAction.getFEIID.failed.heading",
              "Add horse to FEI database and get FEI ID"
            ),
            main: this.$t(
              "horses:horseAction.getFEIID.failed.mainMessage.text",
              "Adding the horse to FEI database failed"
            ),
            reason: this.$t(
              "horses:horseAction.getFEIID.failed.reason.text",
              "The FEI database reported the following reasons:"
            ),
            additional: this.$t(
              "horses:horseAction.getFEIIDfailed.additionalMessage.text",
              "Please contact your technical support, if you need help on this matter or have any questions."
            ),
            okBtn: this.$t("common:ok.button", "OK")
          };
        default:
          return null;
      }
    }
  },
  methods: {
    async submit() {
      this.v$.form.$touch();
      if (this.v$.form.$invalid) return;

      this.formSubmitted = true;

      try {
        const { data } = await this.$apollo.mutate({
          mutation: gql`
            mutation horseCreateInFei(
              $id: ID!
              $representingCountryId: ID
              $confirmationToken: String
            ) {
              horseCreateInFei(
                input: {
                  id: $id
                  representingCountryId: $representingCountryId
                  confirmationToken: $confirmationToken
                }
              ) {
                feedbackMessages {
                  ${DisplayTextFragments.translation}
                }
                success
                horse {
                  id
                  feiid
                }
                duplicates {
                  duplicate {
                    birthCountryCode
                    currentName
                    dateBirth
                    studBookCode
                    microchip
                    ueln
                    horseDamName
                    horseSireName
                    horseSireOfDamName
                    feiCode
                  }
                  ruleDescription
                }
                confirmationToken
              }
            }
          `,
          variables: {
            id: this.horse.id,
            representingCountryId: this.form.representingCountryId,
            confirmationToken: this.confirmationToken
          }
        });

        if (data.horseCreateInFei.success == true) {
          this.responseType = "success";
          this.feedbackMessages = data.horseCreateInFei.feedbackMessages ?? [];
        }

        if (data.horseCreateInFei.success == false) {
          if (data.horseCreateInFei.duplicates?.length > 0) {
            this.responseType = "warning";
            this.confirmationToken = data.horseCreateInFei.confirmationToken;
            this.horseDuplicates = data.horseCreateInFei.duplicates;
          } else {
            this.responseType = "error";
            this.feedbackMessages =
              data.horseCreateInFei.feedbackMessages ?? [];
          }
        }
      } catch (error) {
        this.responseType = "error";
        if (error?.graphQLErrors) {
          this.mutationErrors = error.graphQLErrors;
        }
      } finally {
        this.formSubmitted = false;
      }
    }
  }
});
</script>
